import { MemberRole, TeamRole } from '../enums/userCompanyRole.enum'
import {
  ICompanyMemberPure,
  ICompanyPure,
  ICompany,
  ICompanyMember,
} from '../models/Company'
import {
  IOnboardingInstancePure,
  IOnboardingInstancePopulated,
} from '../models/OnboardingInstance'

// Check if user is lead of a team
export const isLeadOfTeam = (user: ICompanyMemberPure, teamId?: string) => {
  return (
    user.teams.find((team) => '' + team.teamId === '' + teamId)?.role ===
    TeamRole.LEAD
  )
}

// Checks if a user is an admin
export const isAdmin = (member: ICompanyMemberPure) => member.isAdmin

// Checks if a member is a client lead (SUMM-side contact, hidden from the app)
export const isSupport = (member?: ICompanyMemberPure) =>
  member?.role === MemberRole.SUPPORT

// A live company may only be overwritten while it is demonstrably untouched. Past
// this window we assume it is real regardless of login data, which closes the hole
// where a company sits dormant for months and then gets silently replaced.
export const COMPANY_OVERWRITE_WINDOW_DAYS = 30

// Support members are SUMM-side staff, so their logins say nothing about whether the
// customer has started using the company. isDisabled is deliberately NOT checked: a
// member who logged in and was later disabled still left real data behind.
const loggedInMembers = (company: ICompanyPure) =>
  company.members.filter(
    (member) => !isSupport(member) && !!member.lastLoginDate
  )

// Has anyone other than support ever logged in?
export const isCompanyRunning = (company: ICompanyPure) =>
  loggedInMembers(company).length > 0

// The company schema has no top-level creation timestamp and no `timestamps: true`,
// so creation time is derived from the ObjectId — this works retroactively for every
// existing company with no migration. The hex is parsed directly rather than through
// mongoose because this helper is shared with the frontend bundle.
export const getCompanyCreatedAt = (company: ICompanyPure) =>
  new Date(parseInt(String(company._id).slice(0, 8), 16) * 1000)

export interface ICompanyOverwriteLock {
  locked: boolean
  isRunning: boolean
  loggedInMemberCount: number
  ageInDays: number
}

// The single source of truth for "may this company be overwritten?". Both the
// server-side gate and the dry run call this, so they can never disagree. The two
// lock reasons are returned separately because they need different copy in the UI.
//
// Note the age survives a legitimate re-onboard: the overwrite path reuses the
// existing company's _id, so the original ObjectId is preserved. The window is therefore
// measured from first onboarding, not from the last re-run — which is intended.
export const getCompanyOverwriteLock = (
  company: ICompanyPure
): ICompanyOverwriteLock => {
  const loggedInMemberCount = loggedInMembers(company).length
  const ageInDays =
    (Date.now() - getCompanyCreatedAt(company).getTime()) /
    (1000 * 60 * 60 * 24)
  const isRunning = loggedInMemberCount > 0

  return {
    // Either condition alone locks the company.
    locked: isRunning || ageInDays >= COMPANY_OVERWRITE_WINDOW_DAYS,
    isRunning,
    loggedInMemberCount,
    ageInDays,
  }
}

// Member is considered to be a strict lead of someone IF they are the custom lead of the person, OR they are the team lead of the other member
// and the other member does not have a custom lead
// In general, team leads lose some privileges when there is a custom lead. If that is the case, this function needs to be used
export const isMemberStrictLeadOfMember = (
  lead: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  if ('' + lead._id === '' + member._id) {
    return false
  }

  // If the member has custom leads, only those are the leads of the member
  if (member.customLeadIds?.length) {
    return isMemberCustomLeadOfMember(lead, member)
  }

  return isMemberGenerallyLeadOfMember(lead, member)
}

// Simply checks if someone is one of the custom leads of someone else
export const isMemberCustomLeadOfMember = (
  lead: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  if ('' + lead._id === '' + member._id) {
    return false
  }

  return !!member.customLeadIds?.some((leadId) => '' + leadId === '' + lead._id)
}

// Member is considered team lead of someone if the member has a role of lead and both of them are in the same team
export const isMemberTeamLeadOfMember = (
  leadCandidate: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  if ('' + leadCandidate._id === '' + member._id) {
    return false
  }

  // Check if the lead is leading teams in which the member is also participating
  // Team leads within the same team are not considered a team lead of each other
  const memberTeamIds =
    member.teams
      .filter((t) => t.role === TeamRole.MEMBER)
      .map((team) => '' + team.teamId) || []
  const leadTeamIds =
    leadCandidate.teams
      .filter((t) => t.role === TeamRole.LEAD)
      .map((team) => '' + team.teamId) || []

  // If any of the teams are equal, the lead is indeed a lead of this member
  if (leadTeamIds.some((item) => memberTeamIds.includes(item))) {
    return true
  }

  return false
}

export const teamHasTeamLead = (teamId: string, company: ICompanyPure) =>
  company.members
    .filter((member) => !member.isDisabled && !isSupport(member))
    .some((member) =>
      member.teams.some(
        (mTeam) =>
          String(mTeam.teamId) === String(teamId) &&
          mTeam.role === TeamRole.LEAD
      )
    )

// Check if member is a buddy of another member
export const isMemberBuddyOfAnotherMember = (
  buddy: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  return (
    member.buddies &&
    member.buddies.some((buddyId) => '' + buddyId === '' + buddy._id)
  )
}

// General condition for someone being a lead of someone else
// See comments in code for the exact checks performed
export const isMemberGenerallyLeadOfMember = (
  leadCandidate: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  // Someone cannot ever be a lead of himself
  if ('' + leadCandidate._id === '' + member._id) {
    return false
  }

  // If the lead candidate is the team lead of the member, then he / she is a lead
  if (isMemberTeamLeadOfMember(leadCandidate, member)) {
    return true
  }

  // If the member has a custom lead, check if the passed lead is that one
  if (isMemberCustomLeadOfMember(leadCandidate, member)) {
    return true
  }

  return false
}

export const getLeadsOfMember = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure[] => {
  return company.members.filter(
    (candidateLead) =>
      !candidateLead.isDisabled &&
      !isSupport(candidateLead) &&
      isMemberGenerallyLeadOfMember(candidateLead, member)
  )
}

// Leads of the member, or — when the member has no lead — the active admins as fallback
export const getLeadsOfMemberOrAdmins = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure[] => {
  const leads = getLeadsOfMember(member, company)
  if (leads.length) {
    return leads
  }

  return company.members.filter(
    (m) =>
      !m.isDisabled &&
      !isSupport(m) &&
      isAdmin(m) &&
      '' + m._id !== '' + member._id
  )
}

export const doesMemberHaveTeamLead = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): boolean => {
  const activeMembers = company.members.filter(
    (m) => !m.isDisabled && !isSupport(m)
  )

  return activeMembers.some((candidateLead) =>
    isMemberGenerallyLeadOfMember(candidateLead, member)
  )
}
export const getTeamLeadOfMember = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure | undefined => {
  const activeMembers = company.members.filter(
    (m) => !m.isDisabled && !isSupport(m)
  )

  return activeMembers.find((candidateLead) =>
    isMemberGenerallyLeadOfMember(candidateLead, member)
  )
}

export const isMemberOnboardingGuide = (
  leadCandidate: ICompanyMemberPure,
  member: ICompanyMemberPure,
  onboardingInstance: IOnboardingInstancePure | IOnboardingInstancePopulated
) => {
  return (
    onboardingInstance.memberId + '' === member._id + '' &&
    leadCandidate._id + '' === onboardingInstance.onboardingBuddyId + ''
  )
}
export const getCustomLeadsOfMember = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure[] => {
  const activeMembers = company.members.filter(
    (m) => !m.isDisabled && !isSupport(m)
  )

  return activeMembers.filter((candidateLead) =>
    isMemberCustomLeadOfMember(candidateLead, member)
  )
}

export const getMemberRole = (
  member?: ICompanyMemberPure
): TeamRole | undefined => {
  if (!member) {
    return undefined
  }
  return member.teams.some((t) => t.role === TeamRole.LEAD)
    ? TeamRole.LEAD
    : TeamRole.MEMBER
}

export const getMembersVisibleToLead = (
  viewer: ICompanyMember,
  company: ICompany
): ICompanyMember[] => {
  if (isAdmin(viewer)) {
    return company.members.filter((m) => !isSupport(m))
  }
  return company.members.filter(
    (m) => !isSupport(m) && isMemberGenerallyLeadOfMember(viewer, m)
  )
}

export const getAccessibleMembersForInsights = (
  company: ICompanyPure,
  authMember: ICompanyMemberPure
): ICompanyMemberPure[] => {
  return company.members.filter(
    (m) =>
      !m.isDisabled &&
      !isSupport(m) &&
      (isAdmin(authMember) || isMemberGenerallyLeadOfMember(authMember, m))
  )
}
